import { Input as BaseInput } from '@base-ui/react/input';
import { useId } from 'react';
import { WarningCircle } from 'shared/icons';

// Classes compartilhadas do estado de erro dos campos de formulário (Input/
// Select/Textarea): borda no token `danger` + mensagem com ícone de alerta,
// anunciada por leitores de tela (role="alert" + aria-invalid/aria-describedby).
export const FIELD_ERROR_BORDER_CLASSES =
    'border-danger-500 dark:border-danger-500 focus:border-danger-600 dark:focus:border-danger-400 focus:ring-danger-500 dark:focus:ring-danger-400';
export const FIELD_WARNING_BORDER_CLASSES =
    'border-warning-400 dark:border-warning-600 focus:border-warning-500 dark:focus:border-warning-400 focus:ring-warning-500 dark:focus:ring-warning-400';
export const FIELD_BORDER_CLASSES =
    'border-mono-300 dark:border-mono-700 focus:border-black dark:focus:border-white focus:ring-black dark:focus:ring-white';

// `error` pode chegar como boolean (só marca o campo) ou string (vira a
// mensagem); `errorMessage` é sempre mensagem. Extrai o texto exibível.
export function FieldErrorText(error, errorMessage) {
    return [error, errorMessage].find((value) => typeof value === 'string' && value.trim().length > 0) || '';
}

export const FieldErrorMessage = ({ id, children }) => (
    <p id={id} role="alert" className="mt-1 flex items-center gap-1.5 text-sm text-danger-600 dark:text-danger-400">
        <WarningCircle size={14} className="flex-shrink-0" aria-hidden="true" />
        <span>{children}</span>
    </p>
);

/** Aviso que NÃO bloqueia (âmbar, anunciado com `aria-live`): pendência, dado suspeito. */
export const FieldWarningMessage = ({ id, children }) => (
    <p
        id={id}
        aria-live="polite"
        className="mt-1 flex items-start gap-1.5 text-sm text-warning-700 dark:text-warning-300"
    >
        <WarningCircle size={14} className="mt-0.5 flex-shrink-0" aria-hidden="true" />
        <span>{children}</span>
    </p>
);

/** Dica neutra abaixo do campo. */
export const FieldHintMessage = ({ id, children }) => (
    <p id={id} className="mt-1 text-xs text-mono-500 dark:text-mono-400">
        {children}
    </p>
);

/**
 * Mensagem de rodapé do campo com a precedência do design system: erro,
 * depois aviso, depois dica — só UMA aparece. Devolve `{ node, id }` para o
 * `aria-describedby` apontar para o que está na tela.
 */
export function ResolveFieldMessage({ fieldId, errorText, warning, hint }) {
    if (errorText) {
        const id = `${fieldId}-error`;
        return { id, node: <FieldErrorMessage id={id}>{errorText}</FieldErrorMessage> };
    }
    if (warning) {
        const id = `${fieldId}-warning`;
        return { id, node: <FieldWarningMessage id={id}>{warning}</FieldWarningMessage> };
    }
    if (hint) {
        const id = `${fieldId}-hint`;
        return { id, node: <FieldHintMessage id={id}>{hint}</FieldHintMessage> };
    }
    return { id: undefined, node: null };
}

/** Linha do label com um nó opcional à direita (contador "37/120", ação). */
export const FieldLabel = ({ htmlFor, label, labelAside = null }) => {
    if (!label && !labelAside) {
        return null;
    }
    if (!labelAside) {
        return (
            <label htmlFor={htmlFor} className="block text-sm font-medium text-mono-700 dark:text-mono-300 mb-1">
                {label}
            </label>
        );
    }
    return (
        <div className="mb-1 flex items-baseline justify-between gap-2">
            <label htmlFor={htmlFor} className="block text-sm font-medium text-mono-700 dark:text-mono-300">
                {label}
            </label>
            <span className="text-xs text-mono-500 dark:text-mono-400">{labelAside}</span>
        </div>
    );
};

/**
 * Campo de texto do DS. Props opcionais (ausentes = comportamento de sempre):
 *   - `hint`: dica cinza abaixo; `warning`: aviso âmbar que não bloqueia;
 *     precedência erro → aviso → dica;
 *   - `labelAside`: nó à direita do label (contador, link);
 *   - `prefix`/`suffix`: adornos dentro da borda ("R$", "kg").
 */
const Input = ({
    label,
    error,
    errorMessage,
    hint,
    warning,
    labelAside,
    prefix,
    suffix,
    type = 'text',
    className = '',
    ...props
}) => {
    const generatedId = useId();
    const fieldId = props.id || generatedId;
    const hasError = Boolean(error || errorMessage);
    const errorText = FieldErrorText(error, errorMessage);
    const message = ResolveFieldMessage({ fieldId, errorText, warning, hint });
    const borderClasses = hasError
        ? FIELD_ERROR_BORDER_CLASSES
        : warning
          ? FIELD_WARNING_BORDER_CLASSES
          : FIELD_BORDER_CLASSES;
    // `px-4` de sempre; os adornos só alargam o lado deles (pl/pr vêm depois
    // de px no CSS do Tailwind, então vencem sem quebrar quem já passa `pr-10`).
    const paddingClasses = `px-4${prefix ? ' pl-10' : ''}${suffix ? ' pr-12' : ''}`;

    const input = (
        <BaseInput
            id={fieldId}
            type={type}
            aria-invalid={hasError || undefined}
            aria-describedby={message.id}
            className={`w-full ${paddingClasses} py-2 text-base border rounded-lg transition-all duration-200 focus:outline-none focus:ring-1
          ${borderClasses}
          bg-white dark:bg-mono-950 text-black dark:text-white placeholder-mono-400 dark:placeholder-mono-600
          disabled:bg-mono-100 dark:disabled:bg-mono-900 disabled:cursor-not-allowed
          ${className}`}
            {...props}
        />
    );

    return (
        <div className="w-full">
            <FieldLabel htmlFor={fieldId} label={label} labelAside={labelAside} />
            {prefix || suffix ? (
                <div className="relative">
                    {prefix ? (
                        <span
                            aria-hidden="true"
                            className="pointer-events-none absolute inset-y-0 left-0 flex w-10 items-center justify-center text-sm text-mono-500 dark:text-mono-400"
                        >
                            {prefix}
                        </span>
                    ) : null}
                    {input}
                    {suffix ? (
                        <span
                            aria-hidden="true"
                            className="pointer-events-none absolute inset-y-0 right-0 flex w-12 items-center justify-center text-sm text-mono-500 dark:text-mono-400"
                        >
                            {suffix}
                        </span>
                    ) : null}
                </div>
            ) : (
                input
            )}
            {message.node}
        </div>
    );
};

export default Input;
